import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import { Box, Typography } from '@mui/material';
import { mainPageRootStyle } from '../styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { getBots, updateBots } from './actions';
import BotsConfigEditForm from './BotsConfigEditForm';
import HandleSelector from '../components/HandleSelector';
import Breadcrumb from '../components/Breadcrumb';


function BotsConfigPage() {
    const dispatch = useDispatch();
    const handles = useSelector(state => state.handle.handles);
    const botsConfigRedux = useSelector(state => state.bots.botsConfigByHandle);
    const userId = useSelector(state => state.auth.user?.uid);
    const navigate = useNavigate();
    const [botsConfig, setBotsConfig] = useState(null);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const selectedHandle = queryParams.get('handle');

    useEffect(() => {
        document.title = 'Bot Configuration - Sky Toolbox';
    }, []);

    // Auto-select first handle if none is selected
    useEffect(() => {
        if (!selectedHandle && handles && handles.length > 0) {
            navigate(`/bots?handle=${handles[0].handle}`, { replace: true });
        }
    }, [selectedHandle, handles, navigate]);

    // Use useMemo to stabilize the handle ID to avoid unnecessary re-renders
    const handleId = useMemo(() => {
        const handle = handles.find(h => h.handle === selectedHandle);
        return handle?.id;
    }, [handles, selectedHandle]);

    useEffect(() => {
        if (handleId) {
            dispatch(getBots(handleId));
        }
    }, [dispatch, handleId]);

    useEffect(() => {
        if (botsConfigRedux) {
            setBotsConfig({ ...botsConfigRedux });
        }
    }, [botsConfigRedux]);

    const handleSelectedHandleChange = (event) => {
        navigate(`/bots?handle=${event.target.value}`);
    };

    const handleSubmit = (event) => {
        const handle = handles.find(handle => handle.handle === selectedHandle);

        if (handle) {
            dispatch(updateBots(handle.id, handle.handle, botsConfig, userId));
        }
    };

    const handleCancel = () => {
        if (handleId) {
            dispatch(getBots(handleId));
        }
    };

    return (
        <Paper style={mainPageRootStyle} sx={{ p: 3 }}>
            <Breadcrumb items={[
                { label: 'Bots Configuration' }
            ]} />
            
            {/* Page Header */}
            <Box 
                sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 3,
                    p: 3,
                    mb: 4,
                    color: 'white',
                }}
            >
                <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
                    ⚙️ Bots Configuration
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Configure automation settings for your bots
                </Typography>
            </Box>

            <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
                <HandleSelector
                    value={selectedHandle}
                    onChange={handleSelectedHandleChange}
                    handles={handles}
                    ariaLabel="Select handle for bot configuration"
                />
            </Box>

            {botsConfig ? <BotsConfigEditForm botsConfig={botsConfig} handleChange={setBotsConfig} handleSubmit={handleSubmit} handleCancel={handleCancel} /> : undefined}
        </Paper >
    );
}

export default BotsConfigPage;
